import {
  Box,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Tbody,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import lodash from 'lodash';
import React, { useState, useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import { typeguards as finleyTypeguards } from '@finley-technologies/finley-types';

import { ReportingEditGlobalRecurringDatePropertyModal } from './ReportingEditGlobalRecurringDatePropertyModal';
import {
  ReportingRecurringDateSetupRow,
  createStaticRecurringDateSetupShell,
  SourceType,
} from './ReportingRecurringDateSetup';
import {
  AddIconButton,
  EditIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

const { cloneDeep } = lodash;

type GlobalRecurringDateConfig = CreditAgreement.Config.GlobalRecurringDate.Config;

const createRecurringDateConfigShell = (
  idx: number,
): GlobalRecurringDateConfig => {
  return {
    field: `[ placeholderDate${idx} ]`,
    setup: createStaticRecurringDateSetupShell(),
    maxCount: 5,
    defaultTo: { value: '', parseString: false },
  };
};

const ReportingRecurringDateConfigField: React.FC<{
  wipConfig?: WipConfig;
  recurringDateConfig: GlobalRecurringDateConfig;
  basePropertyKey: string;
  idx: number;
  source: SourceType;
}> = ({ wipConfig, recurringDateConfig, basePropertyKey, idx, source }) => {
  const toast = useToast();

  const [
    isEditPropertyModalOpen,
    setIsEditPropertyModalOpen,
  ] = useState<boolean>(false);
  const [isConfigOpen, setIsConfigOpen] = useState<boolean>(false);

  const deleteRecurringDateConfigField = (): void => {
    if (!wipConfig) {
      return;
    }

    const recurringDateConfigs = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isGlobalRecurringDateConfigs(
        recurringDateConfigs,
      ),
      `Retrieved non Recurring Date Config type for ${basePropertyKey}`,
    );

    wipConfig.formProps.setFieldValue(
      basePropertyKey,
      recurringDateConfigs.filter((_unused, loopIdx) => idx !== loopIdx),
    );
  };

  const onSave = useCallback(
    (
      newField: GlobalRecurringDateConfig,
      options?: { ignoreRecurringDateSetupValidate?: boolean },
    ): void => {
      if (!wipConfig) {
        return;
      }

      const isValid = finleyTypeguards.creditAgreement.isGlobalRecurringDateConfig(
        newField,
      );
      if (!isValid) {
        toast({
          description:
            'Attempted to save invalid Recurring Date Config structure',
          status: 'error',
          position: 'top',
          duration: 5000,
        });
        return;
      }

      const wipRecurringDateConfigCopy = cloneDeep(
        getIn(wipConfig.formProps.values, basePropertyKey),
      );
      finleyAssert(
        finleyTypeguards.creditAgreement.isGlobalRecurringDateConfigs(
          wipRecurringDateConfigCopy,
        ),
        `Retrieve non Recurring Date Config type for ${basePropertyKey}`,
      );

      const updatedRecurringDates = wipRecurringDateConfigCopy.map(
        (curConfig, loopIdx) =>
          idx === loopIdx
            ? { ...newField, field: newField.field.trim() }
            : curConfig,
      );

      const recurringDateValidate = recurringDate.validateGlobalRecurringDateConfigs(
        { globalRecurringDateConfigs: updatedRecurringDates, options },
      );
      if (recurringDateValidate.errors.length > 0) {
        toast({
          title: 'Global Recurring Date Error',
          description: recurringDateValidate.errors.join(', '),
          status: 'error',
          position: 'top',
          isClosable: true,
        });
        return;
      }

      wipConfig.formProps.setFieldValue(basePropertyKey, updatedRecurringDates);
    },
    [wipConfig, basePropertyKey, toast, idx],
  );

  const { field, maxCount, defaultTo } = recurringDateConfig;

  return (
    <>
      <Tr>
        <Td>{field}</Td>
        <Td>{maxCount}</Td>
        <Td>{defaultTo.value === '' ? "''" : defaultTo.value}</Td>
        <Td>{String(defaultTo.parseString).toUpperCase()}</Td>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <Td>
            <EditIconButton
              ariaLabel="edit field"
              label="Edit Field"
              onClick={() => {
                setIsEditPropertyModalOpen(true);
              }}
            />
            <DeleteIconButton
              ariaLabel="delete field"
              label="Delete Field"
              onClick={() => {
                deleteRecurringDateConfigField();
              }}
            />
          </Td>
        )}

        <Td>
          <IconButton
            aria-label={`${isConfigOpen ? 'close' : 'display'} config`}
            label={`${isConfigOpen ? 'Close' : 'Display'} Config`}
            icon={isConfigOpen ? <FiChevronUp /> : <FiChevronDown />}
            onClick={() => setIsConfigOpen((prev) => !prev)}
            variant="ghost"
          />
        </Td>
      </Tr>

      {isEditPropertyModalOpen && (
        <ReportingEditGlobalRecurringDatePropertyModal
          wipConfig={wipConfig}
          recurringDateConfig={recurringDateConfig}
          isOpen={isEditPropertyModalOpen}
          onCancel={() => {
            setIsEditPropertyModalOpen(false);
          }}
          onSave={onSave}
        />
      )}

      {isConfigOpen && (
        <ReportingRecurringDateSetupRow
          wipConfig={wipConfig}
          recurringDateConfig={recurringDateConfig}
          basePropertyKey={`${basePropertyKey}[${idx}].setup`}
          onSave={onSave}
          source={source}
        />
      )}
    </>
  );
};

export const ReportingGlobalRecurringDateConfigs: React.FC<{
  globalRecurringDateConfigs: GlobalRecurringDateConfig[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  source: SourceType;
}> = ({ wipConfig, basePropertyKey, globalRecurringDateConfigs, source }) => {
  const addRecurringDateConfigField = (): void => {
    if (!wipConfig) {
      return;
    }

    const currentConfigs = getIn(wipConfig.formProps.values, basePropertyKey);
    const configsCopy = cloneDeep(currentConfigs);
    const field = createRecurringDateConfigShell(configsCopy.length);
    configsCopy.push(field);
    wipConfig.formProps.setFieldValue(basePropertyKey, configsCopy);
  };

  return (
    <Box id="reporting-global-recurring-date-configs-container" mb="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          Recurring Date Configs
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add recurring date config"
            label="Add Recurring Date Config"
            onClick={() => {
              addRecurringDateConfigField();
            }}
          />
        )}
      </Flex>

      {globalRecurringDateConfigs.length > 0 ? (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              <Th w="25%">Field</Th>
              <Th>Max Count</Th>
              <Th>Default Value</Th>
              <Th>Default Parse</Th>
              {wipConfig?.isMutateAllowed(basePropertyKey) && (
                <Th w="5">Edit/Delete</Th>
              )}
              <Th w="3" />
            </Tr>
          </Thead>
          <Tbody>
            {globalRecurringDateConfigs.map((recurringDateConfig, idx) => (
              <ReportingRecurringDateConfigField
                key={recurringDateConfig.field}
                wipConfig={wipConfig}
                recurringDateConfig={recurringDateConfig}
                basePropertyKey={basePropertyKey}
                idx={idx}
                source={source}
              />
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text p="4" border="1px" borderColor="gray.700">
          There are currently no recurring date configs set up.
        </Text>
      )}
    </Box>
  );
};
